import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MhsApplication, MhsApplicationResponse } from '../model/mhs-application';
import { EnvService } from 'src/app/env-service/env.service';
import { EmployeeMhs } from '../model/session/employee-mhs';
import { ApplicantMhs } from '../model/session/applicant-mhs';
import { PartialSaveResp } from '../model/partial-save-resp';

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {
  constructor(private http: HttpClient, private envService: EnvService) { }

  submitCloudApplication(formNumber: string, application: MhsApplication): Observable<MhsApplicationResponse> {
    const url = environment.getApiUrl(environment.mhsApplicationWithExStreamCloudUrl(formNumber),
      this.envService.mhsApiEndpoint,
      this.envService.apiSubscriptionId);
    return this.http.post<MhsApplicationResponse>(url, application);
  }

  submitPartialCloudApplication(formNumber: string, application: MhsApplication): Promise<PartialSaveResp> {
    const url = environment.getApiUrl(environment.mhsPartialApplicationWithExStreamCloudUrl(formNumber),
      this.envService.mhsApiEndpoint,
      this.envService.apiSubscriptionId);
    return firstValueFrom(this.http.post<PartialSaveResp>(url, application));
  }

  submitFinalCloudApplication(mhsId: string, stateOfResidence: string,  policyNumber: string, companyId: string, application: MhsApplication): Observable<PartialSaveResp> {
    const url = environment.getApiUrl(environment.mhsFinalApplicationWithExStreamCloudUrl(mhsId, stateOfResidence, policyNumber, companyId),
      this.envService.mhsApiEndpoint,
      this.envService.apiSubscriptionId);
    return this.http.post<PartialSaveResp>(url, application);
  }

  updatePartialCloudApplication(formNumber: string, mhsId: string, componentName: string, application: MhsApplication): Observable<PartialSaveResp> {
    const url = environment.getApiUrl(environment.updateMhsPartialApplicationWithExStreamCloudUrl(formNumber, mhsId, componentName),
      this.envService.mhsApiEndpoint,
      this.envService.apiSubscriptionId);
    return this.http.post<PartialSaveResp>(url, application);
  }

  updateEmployeeWithExStreamCloudApplication(mhsId: string, employee: EmployeeMhs): Promise<PartialSaveResp> {
    const url = environment.getApiUrl(environment.updateEmployeeWithExStreamCloudUrl(mhsId),
      this.envService.mhsApiEndpoint,
      this.envService.apiSubscriptionId);
    return firstValueFrom(
      this.http.post<PartialSaveResp>(url, employee).pipe(
        catchError((error) => {
          // Handle 304 Not Modified as a successful response
          if (error.status === 304) {
            const resp = new PartialSaveResp();
            resp.httpStatusCode = 304;
            resp.message = 'Not Modified';
            resp.data = { mhsId: mhsId };
            return [resp];
          }
          // Re-throw other errors
          throw error;
        })
      )
    );
  }

  updateApplicantWithExStreamCloudApplication(mhsId: string, applicant: ApplicantMhs): Promise<PartialSaveResp> {
    const url = environment.getApiUrl(environment.updateApplicantWithExStreamCloudUrl(mhsId),
      this.envService.mhsApiEndpoint,
      this.envService.apiSubscriptionId);
    return firstValueFrom(
      this.http.post<PartialSaveResp>(url, applicant).pipe(
        catchError((error) => {
          // Handle 304 Not Modified as a successful response
          if (error.status === 304) {
            const resp = new PartialSaveResp();
            resp.httpStatusCode = 304;
            resp.message = 'Not Modified';
            resp.data = { mhsId: mhsId };
            return [resp];
          }
          // Re-throw other errors
          throw error;
        })
      )
    );
  }

  updateMedicalHistoryWithExStreamCloudApplication(mhsId: string, medicalHistory: any, isNewGenericDynamicForm: boolean): Promise<PartialSaveResp> {
    const url = environment.getApiUrl(environment.updateMedicalHistoryWithExStreamCloudUrl(mhsId, isNewGenericDynamicForm),
      this.envService.mhsApiEndpoint,
      this.envService.apiSubscriptionId);
      
    return firstValueFrom(
      this.http.post<PartialSaveResp>(url, medicalHistory).pipe(
        catchError((error) => {
          // Handle 304 Not Modified as a successful response
          if (error.status === 304) {
            const resp = new PartialSaveResp();
            resp.httpStatusCode = 304;
            resp.message = 'Not Modified';
            resp.data = { mhsId: mhsId };
            return [resp];
          }
          // Re-throw other errors
          throw error;
        })
      )
    );
  }

  updatePhysicianWithExStreamCloudApplication(mhsId: string, physician: any): Promise<PartialSaveResp> {
    const url = environment.getApiUrl(environment.updatePhysicianWithExStreamCloudUrl(mhsId),
      this.envService.mhsApiEndpoint,
      this.envService.apiSubscriptionId);
    return firstValueFrom(
      this.http.post<PartialSaveResp>(url, physician).pipe(
        catchError((error) => {
          // Handle 304 Not Modified as a successful response
          if (error.status === 304) {
            const resp = new PartialSaveResp();
            resp.httpStatusCode = 304;
            resp.message = 'Not Modified';
            resp.data = { mhsId: mhsId };
            return [resp];
          }
          // Re-throw other errors
          throw error;
        })
      )
    );
  }

  updateAcknowledgementsWithExStreamCloudApplication(mhsId: string, acknowledgements: string): Observable<PartialSaveResp> {
    const url = environment.getApiUrl(environment.updateAcknowledgementsWithExStreamCloudUrl(mhsId),
      this.envService.mhsApiEndpoint,
      this.envService.apiSubscriptionId);
    return this.http.post<PartialSaveResp>(url, acknowledgements);
  }

  updateElectronicCommunicationWithExStreamCloudApplication(mhsId: string, electronicCommunication: any): Observable<PartialSaveResp> {
    const url = environment.getApiUrl(environment.updateElectronicCommunicationWithExStreamCloudUrl(mhsId),
      this.envService.mhsApiEndpoint,
      this.envService.apiSubscriptionId);
    return this.http.post<PartialSaveResp>(url, electronicCommunication);
  }

  updateEconsentWithExStreamCloudApplication(mhsId: string, econsent: any): Observable<PartialSaveResp> {
    const url = environment.getApiUrl(environment.updateEconsentWithExStreamCloudUrl(mhsId),
      this.envService.mhsApiEndpoint,
      this.envService.apiSubscriptionId);
    return this.http.post<PartialSaveResp>(url, econsent);
  }

  updateFlowStatusToReady(mhsId: string): Observable<HttpStatusCode> {
    const url = environment.getApiUrl(environment.updateFlowStatusToReady(mhsId),
      this.envService.mhsApiEndpoint,
      this.envService.apiSubscriptionId);
    return this.http.post<HttpStatusCode>(url, {});
  }

  getCloudApplicationPdf(formNumber: string, mhsId: string, pdfId: string): Observable<BlobPart> {
    const url = environment.getApiUrl(environment.mhsCloudPdfUrl(formNumber, mhsId),
      this.envService.mhsApiEndpoint,
      this.envService.apiSubscriptionId);      
    const headers = new HttpHeaders().set('pdfId', pdfId);
    const httpOptions = {
      headers,
      responseType: 'blob' as 'json'
    };
    return this.http.get<BlobPart>(url, httpOptions);
  }
}
